import { Button } from "@/components/ui/UIElements/Button";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  useUrlStates,
  useFranchises,
  useDeleteFranchise,
} from "@finitless/shared/hooks";
import { PageHeader } from "@finitless/shared/components";
import { useToastContext } from "@finitless/shared/contexts";
import { Icon, Modal } from "../../ui";
import Pagination from "../../ui/Data/Pagination";
import { BrandCard, CreateBrandModal } from "../../ui/Brands";
import "./brands-ordering.css";

const ITEMS_PER_PAGE = 10;
const MAX_VISIBLE_PAGES = 5;

const BrandsPage = () => {
  const { t } = useTranslation("brands");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToastContext();
  const [urlStates, updateUrlState] = useUrlStates({
    currentPage: "1",
  });

  const [currentPage, setCurrentPage] = useState(
    parseInt(urlStates?.currentPage) || 1,
  );
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [brandToDelete, setBrandToDelete] = useState(null);
  const searchInputRef = useRef(null);
  const deleteCancelRef = useRef(null);
  const [deleteFailed, setDeleteFailed] = useState(false);

  const { mutateAsync: deleteFranchise, isPending: isDeleting } =
    useDeleteFranchise();

  const { data, isLoading, error } = useFranchises({
    page: currentPage,
    pageSize: ITEMS_PER_PAGE,
  });

  const allBrands = data?.franchises || [];
  const paginationData = data?.pagination;

  // Local search filter
  const brands = allBrands.filter((brand) => {
    if (!searchInput.trim()) return true;
    const searchLower = searchInput.toLowerCase();
    return (
      brand.name?.toLowerCase().includes(searchLower) ||
      brand.slug?.toLowerCase().includes(searchLower) ||
      brand.internal_name?.toLowerCase().includes(searchLower)
    );
  });

  const totalPages =
    paginationData?.totalPages ||
    Math.ceil((paginationData?.total || 0) / ITEMS_PER_PAGE);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    updateUrlState("currentPage", page.toString());
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleViewDetails = (brand) => {
    navigate(`/menu/brands/${brand.id}`);
  };

  const handleCreateBrand = () => {
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
  };

  const handleDeleteBrand = (brand) => {
    setDeleteFailed(false);
    setBrandToDelete(brand);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteFailed(false);
    setShowDeleteModal(false);
    setBrandToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!brandToDelete || isDeleting) return;
    setDeleteFailed(false);

    try {
      await deleteFranchise(brandToDelete.id);
      toast.success(t("toasts.deleteSuccess"));
      handleCloseDeleteModal();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(t("toasts.deleteError"));
      setDeleteFailed(true);
    }
  };

  useEffect(() => {
    if (deleteFailed && !isDeleting) deleteCancelRef.current?.focus();
  }, [deleteFailed, isDeleting]);

  const handleSearchChange = (term) => {
    setSearchInput(term);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    searchInputRef.current?.focus();
  };

  useEffect(() => {
    if (searchParams.get("create") !== "true") {
      return;
    }

    setShowCreateModal(true);

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete("create");
    setSearchParams(nextSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <div
      className="ordering-brands-admin space-y-6"
      data-ordering-expression="product"
    >
      {/* Header */}
      <div className="ordering-brands-header flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <PageHeader title={t("header.title")} subtitle={t("header.subtitle")} />
        <Button
          variant="default"
          type="button"
          onClick={handleCreateBrand}
          className="flex items-center space-x-2 px-4 py-2"
        >
          <Icon name="Plus" className="h-5 w-5" />
          <span>{t("actions.createBrand")}</span>
        </Button>
      </div>

      {/* Search */}
      <div className="ordering-brands-panel border-border bg-background rounded-lg border p-3 sm:p-4">
        <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:gap-4">
          <div className="min-w-0 flex-1">
            <div className="relative">
              <div className="ordering-brands-search-icon pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Icon
                  name="Search"
                  className="text-muted-foreground h-4 w-4 sm:h-5 sm:w-5"
                />
              </div>
              <input
                ref={searchInputRef}
                type="text"
                aria-label={t("search.placeholder")}
                placeholder={t("search.placeholder")}
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="ordering-brands-search-input border-border bg-card focus:ring-ring h-9 w-full rounded-lg border px-3 py-2 pr-8 pl-8 text-sm text-white placeholder-gray-400 focus:border-transparent focus:ring-2 focus:outline-none sm:h-10 sm:px-4 sm:pr-10 sm:pl-10"
              />
              {searchInput && (
                <Button
                  variant="ghost"
                  onClick={handleClearSearch}
                  aria-label={t("stores:search.clearSearch")}
                  className="ordering-brands-search-clear bg-muted hover:bg-muted absolute top-1/2 right-2 flex h-4 w-4 -translate-y-1/2 transform cursor-pointer items-center justify-center rounded-full transition-colors sm:h-5 sm:w-5"
                  type="button"
                >
                  <svg
                    className="text-foreground h-2.5 w-2.5 hover:text-white sm:h-3 sm:w-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div
          className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
          role="status"
          aria-label={t("detailPage.states.loading")}
        >
          {[...Array(6)].map((_, index) => (
            <div
              key={index}
              className="border-border bg-background animate-pulse rounded-lg border-2 p-4"
            >
              <div className="space-y-2">
                <div className="flex items-start space-x-3">
                  <div className="bg-card h-12 w-12 flex-shrink-0 rounded-lg"></div>
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="bg-card h-4 w-3/4 rounded"></div>
                    <div className="bg-card h-3 w-1/2 rounded"></div>
                  </div>
                </div>
              </div>
              <div className="border-border mt-2 border-t pt-3">
                <div className="bg-card ml-auto h-3 w-1/3 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="py-12 text-center" role="alert">
          <Icon
            name="AlertCircle"
            className="text-destructive mx-auto mb-4 h-12 w-12"
          />
          <h3 className="mb-2 text-lg font-medium text-white">
            {t("error.title")}
          </h3>
          <p className="text-muted-foreground">
            {error?.message || t("error.defaultMessage")}
          </p>
        </div>
      ) : brands.length > 0 ? (
        <>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {brands.map((brand) => (
              <BrandCard
                key={brand.id}
                brand={brand}
                onViewDetails={() => handleViewDetails(brand)}
                onDelete={handleDeleteBrand}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-8">
              <div className="ordering-brands-panel border-border bg-background rounded-lg border p-4">
                <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
                  {/* Pagination Info */}
                  <div className="text-muted-foreground text-sm">
                    {t("pagination.showing", {
                      from: (currentPage - 1) * ITEMS_PER_PAGE + 1,
                      to: Math.min(
                        currentPage * ITEMS_PER_PAGE,
                        paginationData?.total || 0,
                      ),
                      total: paginationData?.total || 0,
                    })}
                    {searchInput && (
                      <span className="ml-2">
                        {t("pagination.filtered", { count: brands.length })}
                      </span>
                    )}
                  </div>

                  {/* Pagination Controls */}
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    maxVisiblePages={MAX_VISIBLE_PAGES}
                    disabled={isLoading}
                    className="mt-4 sm:mt-0"
                  />
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="py-12 text-center">
          <Icon
            name="Building"
            className="text-muted-foreground mx-auto mb-4 h-12 w-12"
          />
          <h3 className="mb-2 text-lg font-medium text-white">
            {t("empty.title")}
          </h3>
          <p className="text-muted-foreground mb-6">{t("empty.subtitle")}</p>
          <Button
            variant="default"
            type="button"
            onClick={handleCreateBrand}
            className="mx-auto flex items-center space-x-2 px-4 py-2"
          >
            <Icon name="Plus" className="h-5 w-5" />
            <span>{t("actions.createBrand")}</span>
          </Button>
        </div>
      )}

      {/* Create Brand Modal */}
      {showCreateModal && (
        <CreateBrandModal
          isOpen={showCreateModal}
          onClose={handleCloseCreateModal}
        />
      )}

      {/* Delete Brand Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        title={t("deleteModal.title")}
        subtitle={t("deleteModal.subtitle")}
        maxWidth="max-w-md"
      >
        {brandToDelete && (
          <div className="ordering-brands-modal space-y-4">
            <div className="border-destructive bg-destructive/10 dark:border-destructive dark:bg-destructive/20 rounded-lg border p-4">
              <div className="flex items-center space-x-3">
                <Icon
                  name="AlertTriangle"
                  className="text-destructive h-5 w-5"
                />
                <div>
                  <h4 className="text-destructive dark:text-destructive text-sm font-medium">
                    {brandToDelete.name}
                  </h4>
                  <p className="text-destructive dark:text-destructive mt-1 text-xs">
                    {t("deleteModal.slugLabel")}: {brandToDelete.slug}
                  </p>
                </div>
              </div>
            </div>

            <p className="text-muted-foreground text-sm">
              {t("deleteModal.warning")}
            </p>

            <div className="ordering-brands-modal-actions flex space-x-3 pt-4">
              <Button
                variant="destructive"
                type="button"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                aria-busy={isDeleting}
                className="flex flex-1 items-center justify-center space-x-2 px-4 py-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isDeleting ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                ) : null}
                <span>
                  {isDeleting
                    ? t("deleteModal.actions.deleting")
                    : t("deleteModal.actions.deleteBrand")}
                </span>
              </Button>
              <Button
                variant="ghost"
                type="button"
                onClick={handleCloseDeleteModal}
                ref={deleteCancelRef}
                disabled={isDeleting}
                autoFocus
                className="text-card-foreground bg-card border-border hover:bg-accent flex-1 rounded-lg border px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t("deleteModal.actions.cancel")}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default BrandsPage;
